/* eslint-disable import/no-extraneous-dependencies */

import { GridColDef } from '@mui/x-data-grid';
import {
  ApiError,
  BrowseView,
  browseViewColumnAction,
  defaultBrowseViewColumnActions,
  useBrowseViewRef,
} from '@pro4-erp/core-web';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { useEffect } from 'react';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AuditInvitation, getAuditInvitationsForMe } from '@/common';
import { useBrowsePageStore } from './useBrowsePageStore';
import DenyAuditInvitationModal from './DenyAuditInvitationModal';
import ConfirmAuditInvitationModal from './ConfirmAuditInvitationModal';
import DenyStatementModal from './DenyStatementModal';
import { getBeautifiedLocalDate } from '@/core';
import { AuditorVisitInvitationStatement } from '../../types';
import { getAuditStatement } from '../../api/getAuditStatement';

const BrowsePage = () => {
  const { t, i18n } = useTranslation();
  const browsePageStore = useBrowsePageStore();
  const navigate = useNavigate();
  const browseViewRef = useBrowseViewRef();
  const lang = i18n.language;
  const query = useQuery<AuditorVisitInvitationStatement, ApiError>(
    ['auditor-visits-invitations-statement', lang],
    () => getAuditStatement(lang),
    { refetchOnWindowFocus: false }
  );

  useEffect(() => {
    browsePageStore.setBrowseViewRef(browseViewRef);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseViewRef]);

  const columns: GridColDef<AuditInvitation>[] = [
    {
      ...browseViewColumnAction<AuditInvitation>({
        minWidth: 60,
        field: 'Confirm',

        headerName: t('Confirm', { defaultValue: 'Confirm' }).toString(),
        icon: {
          element: <CheckIcon />,
          color: 'primary',
        },
        onClick: ({ params }) => {
          browsePageStore.setAuditInvitation(params.row);
          browsePageStore.setModalState('confirm-audit-invitation');
        },
      }),
      display: 'flex',
    },
    {
      ...browseViewColumnAction<AuditInvitation>({
        minWidth: 60,
        field: 'Deny',
        headerName: t('Deny', { defaultValue: 'Deny' }).toString(),
        icon: {
          element: <CloseIcon />,
          color: 'error',
        },
        onClick: ({ params }) => {
          browsePageStore.setAuditInvitation(params.row);
          browsePageStore.setModalState('deny-audit-invitation');
        },
      }),
      display: 'flex',
    },
    {
      ...browseViewColumnAction({
        minWidth: 60,
        field: 'actions',
        headerName: t('Open', { defaultValue: 'Open' }),
        icon: {
          element: <EditIcon />,
          color: 'primary',
        },
        onClick: ({ params }) => navigate(`/audit-invitations/${params.id}`),
      }),
      display: 'flex',
    },
    {
      field: 'workOrder',
      display: 'flex',
      headerName: t('Work order', { defaultValue: 'Work order' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      sortable: false,
      valueGetter(_, row) {
        return row.workOrder?.name || 'N/A';
      },
    },
    {
      field: 'standards',
      display: 'flex',
      headerName: t('Standards', { defaultValue: 'Standards' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      sortable: false,
      valueGetter(_, row) {
        return row.standards || 'N/A';
      },
    },
    {
      field: 'planedTime',
      display: 'flex',
      headerName: t('Planed time', { defaultValue: 'Planed time' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
    },
    {
      field: 'datStart',
      minWidth: 130,
      display: 'flex',
      headerName: t('Start date', { defaultValue: 'Start date' }).toString(),
      flex: 1.5,
      disableColumnMenu: true,
      valueGetter(_, row) {
        return row.datStart ? getBeautifiedLocalDate(row.datStart) : 'N/A';
      },
    },
    {
      field: 'services',
      display: 'flex',
      headerName: t('Services', { defaultValue: 'Services' }).toString(),
      flex: 1,
      minWidth: 300,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        if (params.row.services) {
          return (
            <List>
              {params.row.services.map((service) => (
                <ListItem key={service.id} sx={{ padding: 0 }}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                    primary={service.serviceShowAs}
                    secondary={
                      <Box>
                        <Typography fontSize="0.8rem">
                          {t('Standard', { defaultValue: 'Standard' })}: {service.processStandard},{' '}
                          {t('Scope of work', { defaultValue: 'Scope of work' })}:{' '}
                          {service.scopeOfWork || 'N/A'},{' '}
                          {t('Scope confirmed', { defaultValue: 'Scope confirmed' })}:{' '}
                          {service.scopeConfirmed || 'N/A'}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          );
        }
        return <> </>;
      },
    },
    {
      field: 'teamMembers',
      display: 'flex',
      headerName: t('Team members', { defaultValue: 'Team members' }).toString(),
      flex: 1,
      minWidth: 300,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        if (params.row.teamMembers) {
          const title = params.row.teamMembers
            .map((member) => `${member.name} ${member.role}`)
            .join(',\n');
          return (
            <ul style={{ paddingLeft: 15 }} className="MuiDataGrid-cellContent" title={title || ''}>
              {params.row.teamMembers.map((member) => (
                <li key={member.id}>
                  {member.name} {member.role}
                </li>
              ))}
            </ul>
          );
        }
        return <> </>;
      },
    },
    {
      field: 'noteForAuditors',
      display: 'flex',
      headerName: t('Note', { defaultValue: 'Note' }).toString(),
      flex: 1,
      minWidth: 500,
      disableColumnMenu: true,
    },
    {
      field: 'status',
      minWidth: 130,
      display: 'flex',
      headerName: t('Status', { defaultValue: 'Status' }).toString(),
      flex: 1.5,
      disableColumnMenu: true,
    },
    {
      field: 'numOfEmployees',
      minWidth: 130,
      display: 'flex',
      headerName: t('Number of employees', { defaultValue: 'Number of employees' }).toString(),
      flex: 1.5,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: 'isMultisite',
      minWidth: 130,
      display: 'flex',
      headerName: t('Is multisite', { defaultValue: 'Is multisite' }).toString(),
      flex: 1.5,
      disableColumnMenu: true,
    },
  ];

  return (
    <>
      <ConfirmAuditInvitationModal osvStatement={query?.data?.Note || null} />
      <DenyAuditInvitationModal />
      <DenyStatementModal />
      <BrowseView
        title={
          <BrowseView.Title>
            {t('Audit invitations', { defaultValue: 'Audit invitations' })}
          </BrowseView.Title>
        }
        browserViewContainerSx={{
          height: '75vh',
        }}
        browseViewRef={browseViewRef}
        columns={columns}
        fetchData={(props) => {
          return getAuditInvitationsForMe({ ...props, lang });
        }}
        toolbar={<BrowseView.Toolbar showSearch />}
        getRowHeight={() => 'auto'}
      />
    </>
  );
};

export default BrowsePage;
